import { loadSessionField } from '../auth/sessionProfileStore'
import React, { useContext, useMemo } from 'react'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { alpha, keyframes } from '@mui/material/styles'
import {
  Archive,
  ArrowRight,
  ArrowUUpLeft,
  CalendarBlank,
  ChartBar,
  ChartLine,
  ClipboardText,
  Coin,
  CurrencyCircleDollar,
  Package,
  ShoppingCart,
  SquaresFour,
  Storefront,
  Truck,
  Users
} from '@phosphor-icons/react'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import cookie from 'react-cookies'
import { useNavigate } from 'react-router-dom'
import { isMfaEnabled } from '../auth'
import FormHeaderDetailActions from '../components/action_button/FormHeaderDetailActions'
import GuidanceNote from '../components/GuidanceNote'
import {
  UI_COLOR_ACCENT_ORANGE,
  UI_COLOR_ACCENT_ORANGE_TEXT,
  UI_COLOR_BORDER_LIGHT,
  UI_COLOR_BRAND_NAVY,
  UI_COLOR_SECONDARY,
  UI_COLOR_TEXT_INK,
  UI_COLOR_TEXT_SUBTITLE,
  UI_COLOR_TEXT_WARM,
  UI_COLOR_WHITE
} from '../constants/uiColors'
import { GlobalContext } from '../global_context/GlobalContext'
import { getHomeCapabilities } from './homeAccess'
import { getHomeShortcuts } from './homeShortcuts'

const HOME_MFA_HINT =
  'Puede activar el doble factor de autenticación por app de autenticación para proteger mejor su cuenta.'

const CARD_SHADOW = '0 1px 4px rgba(0, 0, 0, 0.1)'
const CARD_RADIUS = '12px'
const SHORTCUT_CARD_RADIUS = '8px'

const HOME_SHORTCUT_ICONS = {
  archive: Archive,
  assignment: ClipboardText,
  calendar: CalendarBlank,
  cart: ShoppingCart,
  chartbar: ChartBar,
  chartline: ChartLine,
  clipboard: ClipboardText,
  coin: Coin,
  currency: CurrencyCircleDollar,
  event_note: CalendarBlank,
  inventory_2: Package,
  local_shipping: Truck,
  manage_accounts: Users,
  monitoring: ChartLine,
  package: Package,
  payments: Coin,
  replay: ArrowUUpLeft,
  shopping_cart: ShoppingCart,
  store: Storefront,
  storefront: Storefront,
  truck: Truck,
  users: Users,
  uturn: ArrowUUpLeft
}

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

function greetingForNow() {
  const hour = new Date().getHours()
  if (hour < 12) return 'Buenos días'
  if (hour < 19) return 'Buenas tardes'
  return 'Buenas noches'
}

function formatTodayLabel() {
  try {
    const label = new Intl.DateTimeFormat('es-PA', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    }).format(new Date())
    return label.charAt(0).toUpperCase() + label.slice(1)
  } catch {
    return ''
  }
}

function resolveFirstName() {
  const userName = cookie.load('user_name') || ''
  const userEmail = cookie.load('user_email') || ''
  const source = userName || (userEmail ? userEmail.split('@')[0] : '') || 'Usuario'
  const first = String(source).trim().split(/\s+/)[0] || 'Usuario'
  return first.charAt(0).toUpperCase() + first.slice(1)
}

function HomeShortcutIcon({ name }) {
  const Icon = HOME_SHORTCUT_ICONS[name] || SquaresFour
  return <Icon size={20} weight="regular" />
}

/** Recorte del isotipo completo dentro de /ubiqua_logo_white1.png (1200×1200). */
const UBIQUA_MARK = { src: 1200, x: 162, y: 478, w: 228, h: 243 }

function HomeLogoMask() {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        right: { xs: -85, sm: -90, md: -90 },
        top: '50%',
        width: { xs: 132, sm: 152, md: 168 },
        height: {
          xs: (132 * UBIQUA_MARK.h) / UBIQUA_MARK.w,
          sm: (152 * UBIQUA_MARK.h) / UBIQUA_MARK.w,
          md: (168 * UBIQUA_MARK.h) / UBIQUA_MARK.w
        },
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
        transform: 'translateY(-50%)'
      }}
    >
      <Box
        component="img"
        src="/ubiqua_logo_white1.png"
        alt=""
        sx={{
          position: 'absolute',
          display: 'block',
          m: 0,
          height: `${(UBIQUA_MARK.src / UBIQUA_MARK.h) * 100}%`,
          width: 'auto',
          maxWidth: 'none',
          top: `${(-UBIQUA_MARK.y / UBIQUA_MARK.h) * 100}%`,
          left: `${(-UBIQUA_MARK.x / UBIQUA_MARK.w) * 100}%`,
          mixBlendMode: 'screen',
          opacity: 1,
          maskImage:
            'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.85) 100%)',
          WebkitMaskImage:
            'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.85) 100%)',
          pointerEvents: 'none',
          userSelect: 'none'
        }}
      />
    </Box>
  )
}

export default function HomeIndexApp() {
  const navigate = useNavigate()
  const {
    variants,
    shouldCreateOrder,
    hasAppEntrega,
    hasTenantReport,
    canViewReport
  } = useContext(GlobalContext)

  const capabilities = useMemo(
    () =>
      getHomeCapabilities({
        variants,
        shouldCreateOrder,
        hasAppEntrega,
        hasTenantReport,
        canViewReport
      }),
    [variants, shouldCreateOrder, hasAppEntrega, hasTenantReport, canViewReport]
  )

  const shortcuts = useMemo(() => getHomeShortcuts(capabilities), [capabilities])
  const firstName = resolveFirstName()
  const businessName = loadSessionField('business') || ''
  const todayLabel = formatTodayLabel()
  const canOpenReportes = Boolean(capabilities.reportes)
  const showMfaHint = !isMfaEnabled()

  return (
    <Box
      className="home-index-page"
      sx={{
        position: 'relative',
        minHeight: 'calc(100vh - 70px)',
        px: { xs: 1.5, sm: 2.5, lg: 3 },
        py: { xs: 2, sm: 2.5 },
        pb: 5,
        background: 'linear-gradient(180deg, #F8FAFC 0%, #EFF4F8 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(74, 122, 171, 0.09) 0.9px, transparent 0.9px)',
          backgroundSize: '18px 18px',
          opacity: 0.4
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 1148,
          mx: 'auto'
        }}
      >
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            width: '100%',
            mb: showMfaHint ? 2 : 3.5,
            minHeight: { xs: 214, md: 216 },
            borderRadius: CARD_RADIUS,
            border: `1px solid ${UI_COLOR_BORDER_LIGHT}`,
            boxShadow: `0 10px 28px ${alpha(UI_COLOR_ACCENT_ORANGE, 0.08)}, ${CARD_SHADOW}`,
            bgcolor: UI_COLOR_WHITE,
            background: [
              `radial-gradient(ellipse 70% 140% at 100% 50%, ${alpha(
                UI_COLOR_ACCENT_ORANGE,
                0.22
              )} 0%, ${alpha(UI_COLOR_ACCENT_ORANGE, 0.08)} 38%, transparent 68%)`,
              `linear-gradient(105deg, ${UI_COLOR_WHITE} 0%, ${UI_COLOR_WHITE} 28%, #FFF9F4 52%, #FFE8D4 78%, #FFD4B0 100%)`
            ].join(', '),
            animation: `${fadeUp} 420ms ease-out both`
          }}
        >
          <HomeLogoMask />

          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              px: { xs: 3.25, sm: 3.5, md: 4 },
              py: { xs: 3.5, md: 3.75 },
              maxWidth: { xs: '82%', md: '68%' }
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                mb: 1.5,
                px: 1.35,
                py: 0.45,
                borderRadius: '999px',
                bgcolor: UI_COLOR_WHITE,
                border: `1px solid ${alpha(UI_COLOR_ACCENT_ORANGE_TEXT, 0.28)}`,
                color: UI_COLOR_ACCENT_ORANGE_TEXT,
                fontSize: 12.5,
                fontWeight: 700,
                letterSpacing: 0
              }}
            >
              Panel de inicio
            </Box>

            <Typography
              component="h1"
              sx={{
                fontSize: { xs: 28, sm: 34, md: 38 },
                fontWeight: 800,
                letterSpacing: 0,
                lineHeight: 1.12,
                color: UI_COLOR_BRAND_NAVY
              }}
            >
              {greetingForNow()}, {firstName}
            </Typography>

            <Typography
              sx={{
                mt: 0.85,
                fontSize: 14,
                fontWeight: 500,
                color: UI_COLOR_TEXT_SUBTITLE
              }}
            >
              {todayLabel}
              {businessName ? ` · ${businessName}` : ''}
            </Typography>

            {canOpenReportes ? (
              <Button
                variant="contained"
                disableElevation
                onClick={() => navigate('/reportes')}
                endIcon={<ArrowRight size={18} weight="bold" />}
                sx={{
                  mt: 2.2,
                  textTransform: 'none',
                  fontWeight: 800,
                  borderRadius: '8px',
                  px: 2.4,
                  py: 0.85,
                  bgcolor: UI_COLOR_ACCENT_ORANGE,
                  color: UI_COLOR_WHITE,
                  boxShadow: `0 12px 24px ${alpha(UI_COLOR_ACCENT_ORANGE, 0.24)}`,
                  '&:hover': { bgcolor: UI_COLOR_ACCENT_ORANGE_TEXT },
                  '& .MuiButton-endIcon': { ml: 1, mr: 0 },
                  '& .MuiButton-endIcon svg': { m: 0, display: 'block' }
                }}
              >
                Ver Reportes
              </Button>
            ) : null}
          </Box>
        </Box>

        {showMfaHint ? (
          <GuidanceNote
            title=""
            message={HOME_MFA_HINT}
            icon={<ShieldOutlinedIcon />}
            singleLine
            accentColor={UI_COLOR_SECONDARY}
            backgroundColor={UI_COLOR_WHITE}
            textColor={UI_COLOR_TEXT_WARM}
            sx={{
              mb: 3.5,
              border: `1px solid ${UI_COLOR_BORDER_LIGHT}`,
              borderLeft: `4px solid ${UI_COLOR_SECONDARY}`,
              borderRadius: '6px',
              boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)'
            }}
            headerSx={{ width: '100%', columnGap: 1, rowGap: 1 }}
            messageSx={{ fontSize: 13.5, fontWeight: 400, lineHeight: 1.5 }}
            action={
              <FormHeaderDetailActions
                showSecondary={false}
                showPrimaryIcon={false}
                primaryLabel="Activar"
                onPrimary={() => navigate('/perfil/seguridad')}
              />
            }
          />
        ) : null}

        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.5 }}>
          <Box
            sx={{
    
            }}
          />
          <Typography
            component="h2"
            sx={{
              fontSize: 20,
              fontWeight: 800,
              color: UI_COLOR_TEXT_INK,
              letterSpacing: 0
            }}
          >
            Atajos
          </Typography>
        </Stack>

        <div className="index-table home-shortcuts-container">
          <div className="show-area home-shortcuts-area">
            {shortcuts.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: UI_COLOR_TEXT_SUBTITLE }}>
                No hay atajos disponibles para tu rol en este tenant.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))'
                  },
                  gap: 1.5
                }}
              >
                {shortcuts.map((item) => (
                  <Card
                    key={item.id}
                    elevation={0}
                    sx={{
                      minHeight: 68,
                      borderRadius: SHORTCUT_CARD_RADIUS,
                      border: `1px solid ${UI_COLOR_BORDER_LIGHT}`,
                      bgcolor: UI_COLOR_WHITE,
                      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                      overflow: 'hidden',
                      transition:
                        'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                      '&:hover': {
                        borderColor: alpha(UI_COLOR_BRAND_NAVY, 0.18),
                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    <CardActionArea
                      onClick={() => navigate(item.href)}
                      sx={{
                        height: '100%',
                        '&:hover': { bgcolor: alpha(UI_COLOR_BRAND_NAVY, 0.025) }
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: SHORTCUT_CARD_RADIUS,
                              bgcolor: alpha(UI_COLOR_BRAND_NAVY, 0.14),
                              color: UI_COLOR_BRAND_NAVY,
                              flexShrink: 0
                            }}
                          >
                            <HomeShortcutIcon name={item.icon} />
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: UI_COLOR_TEXT_INK,
                                lineHeight: 1.25,
                                overflowWrap: 'anywhere'
                              }}
                            >
                              {item.label}
                            </Typography>
                            {item.description ? (
                              <Typography
                                sx={{
                                  mt: 0.2,
                                  fontSize: 12,
                                  color: UI_COLOR_TEXT_SUBTITLE,
                                  lineHeight: 1.3
                                }}
                              >
                                {item.description}
                              </Typography>
                            ) : null}
                          </Box>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Box>
            )}
          </div>
        </div>
      </Box>
    </Box>
  )
}
